import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContinueType } from '@myriadgenetics/mgh-types';
import { H4, Text2 } from '../../../elements/typography';
import Button from '../../../elements/button';
import { startOver, useHistory } from '../../../helpers/routing';
import { isChatbotUrl, isSimonMedUrl, useUrl } from '../../../helpers/url';
import Logger from '../../../helpers/logger';

interface CWProps {
  continueKey?: string;
  allAreValid: boolean;
  continueType: ContinueType;
  isResultsPage?: boolean;
  isAuthPage?: boolean;
  triggerContinue: () => void;
  isClosePageButtonExist?: boolean;
  gtmDataTracking?: () => void;
  hasBackButton?: boolean;
  isChatbotStyle?: boolean;
  isTriggerModal?: boolean;
  disabled?: boolean;
  disabledIcon?: any;
}

function ContinueWrapper({
  continueKey,
  isResultsPage,
  isAuthPage,
  continueType,
  allAreValid,
  hasBackButton,
  triggerContinue,
  isClosePageButtonExist,
  gtmDataTracking,
  isChatbotStyle,
  isTriggerModal,
  disabled,
  disabledIcon,
}: CWProps) {
  const { t } = useTranslation();
  const { urlType } = useUrl();
  const history = useHistory();
  const goBack = () => {
    Logger.logEvent('Clicked Back Button', null);
    history.goBack();
  };

  const disabledButtonText = useCallback(
    (key: string) => {
      const originalButtonText = t(key);

      if (!isTriggerModal) return originalButtonText;

      /**
       * This `const` strips the word "Enter" from the
       * beginning of the button text after translation
       */
      const finalButtonText = originalButtonText.replace(`${t('chatbot.enter')} `, '');

      return finalButtonText;
    },
    [isTriggerModal, t],
  );

  // TODO: Refactoring: These conditional renderings are difficult to manage and to read.
  const isChatbotAndValidAndDisabled = isChatbotStyle && allAreValid && disabled;
  const isNotChatbotAndNotResults = !isChatbotStyle && !isResultsPage;
  const isChatbotAndValidAndNotDisabled = isChatbotStyle && allAreValid && !disabled;
  const shouldHideContinueButton = continueType === ContinueType.AutoFixed;

  const showContinueButton = useMemo(() => {
    if (allAreValid || !shouldHideContinueButton) {
      return true;
    }

    return false;
  }, [allAreValid, shouldHideContinueButton]);

  const handleOnContinueWithGtmLog = () => {
    triggerContinue();
    gtmDataTracking && gtmDataTracking();
  };

  return (
    <>
      {hasBackButton && (
        <Button onClick={goBack} className={`back-button ${isSimonMedUrl() ? 'back-button__smi' : ''}`} isBackButton={hasBackButton}>
          {t('back')}
        </Button>
      )}
      {isChatbotAndValidAndDisabled && (
        <div className="container-after-modal-closed continue__button">
          {isTriggerModal && !isResultsPage && (
            <Button className="chat-bubble--undo chat-bubble--edit" onClick={handleOnContinueWithGtmLog}>
              {t('edit')}
            </Button>
          )}
          <div className="wrapper">
            {disabledIcon}
            <Text2>{disabledButtonText(continueKey || 'continue')}</Text2>
          </div>
        </div>
      )}
      {urlType !== 'classic' && (isNotChatbotAndNotResults || isChatbotAndValidAndNotDisabled) && (
        <div className={`page-node__action page-node__action--continue ${isTriggerModal ? 'page-node__action--modal-trigger' : ''}`}>
          <Button
            className={isSimonMedUrl() ? 'smi__continue-button' : 'page-node__continue-button'}
            dataTestId="continue-button"
            onClick={handleOnContinueWithGtmLog}
            isChatbotStyle={isChatbotStyle || isChatbotUrl(window?.location?.href)}
            disabled={!allAreValid && true}
            showContinueButton={showContinueButton}
          >
            {t(continueKey || 'continue')}
          </Button>
        </div>
      )}
      {urlType === 'classic' && isAuthPage && !isResultsPage && (
        <div className={`page-node__action page-node__action--continue ${isTriggerModal ? 'page-node__action--modal-trigger' : ''}`}>
          <Button
            className={'page-node__continue-button'}
            dataTestId="continue-button"
            onClick={handleOnContinueWithGtmLog}
            isChatbotStyle={false}
            disabled={!allAreValid && true}
            showContinueButton={showContinueButton}
          >
            {t(continueKey || 'continue')}
          </Button>
        </div>
      )}
      {urlType === 'classic' && !isAuthPage && !isResultsPage && (
        <div className={`page-node__action page-node__action--continue ${isTriggerModal ? 'page-node__action--modal-trigger' : ''}`}>
          <Button onClick={goBack} className={`back-button`} isBackButton>
            {t('back')}
          </Button>
          <Button
            className={'page-node__continue-button'}
            dataTestId="continue-button"
            onClick={handleOnContinueWithGtmLog}
            isChatbotStyle={false}
            disabled={!allAreValid && true}
            showContinueButton={showContinueButton}
          >
            {t(continueKey || 'continue')}
          </Button>
          {/* Hiding duplicate back buttons with styles in order to preserve tab order */}
          <Button onClick={goBack} className={`back-button`} isBackButton>
            {t('back')}
          </Button>
        </div>
      )}
      {!isChatbotStyle && isResultsPage && (
        <>
          {/* Preserve you are done text for Galileo and VC results */}
          {(urlType === 'galileo' || urlType === 'vc') && (
            <div className="page-node__start-over-break">
              <hr className="page-node__results-break" />
              <H4>{t('startOverBreakText')}</H4>
            </div>
          )}
          {/* TODO Temporary decision. When we have buttons in related nodes - remove this button */}
          {
            <div className={`page-node__action page-node__action--continue`}>
              <Button className="page-node__action--start-over__button page-node__continue-button" onClick={startOver}>
                {t('results.close')}
              </Button>
            </div>
          }
        </>
      )}
    </>
  );
}

export default ContinueWrapper;
